import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface Column<T> {
  key: string;
  label: string;
  width: number;
  visible: boolean;
  sortable?: boolean;
  render?: (item: T, column: Column<T>) => React.ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onColumnsChange?: (columns: Column<T>[]) => void;
  idField: string;
  onDelete?: (ids: string[]) => void;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  showColumnCustomizer?: boolean;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onPageChange?: (page: number) => void;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  selectable?: boolean;
  onRowClick?: (item: T) => void;
  customActions?: React.ReactNode;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns: initialColumns,
  onColumnsChange,
  idField,
  onDelete,
  onSort,
  sortField,
  sortOrder,
  showColumnCustomizer = true,
  pagination,
  onPageChange,
  loading,
  error,
  emptyMessage = 'No data found',
  selectable = true,
  onRowClick,
  customActions
}: DataTableProps<T>) {
  const [columns, setColumns] = useState(initialColumns);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showColumnCustomizerModal, setShowColumnCustomizerModal] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [pageInput, setPageInput] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);

  // Update columns when prop changes
  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  // Clear selections when data changes
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAll(false);
  }, [data]);

  // Update selectAll state based on current selections
  useEffect(() => {
    if (data.length > 0) {
      const allSelected = data.every(item => selectedIds.has(item[idField]));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedIds, data, idField]);

  const handleColumnUpdate = (newColumns: Column<T>[]) => {
    setColumns(newColumns);
    onColumnsChange?.(newColumns);
  };

  const toggleSelectItem = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(data.map(item => item[idField])));
      setSelectAll(true);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0 || !onDelete) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} selected items?`)) {
      onDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectAll(false);
    }
  };

  const handleSort = (field: string) => {
    if (!onSort) return;
    
    const column = columns.find(c => c.key === field);
    if (!column?.sortable) return;

    let newOrder: 'asc' | 'desc' = 'desc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    
    onSort(field, newOrder);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pagination || !onPageChange) return;
    
    const page = parseInt(pageInput);
    if (page >= 1 && page <= pagination.totalPages) {
      onPageChange(page);
      setPageInput('');
    }
  };

  // Column customization handlers
  const toggleColumnVisibility = (index: number) => {
    const newColumns = [...columns];
    newColumns[index].visible = !newColumns[index].visible;
    handleColumnUpdate(newColumns);
  };

  const handleCustomizerDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
  };

  const handleCustomizerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleCustomizerDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, movedColumn);
    handleColumnUpdate(newColumns);
    setDraggedColumn(null);
  };

  const visibleColumns = columns.filter(col => col.visible);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">❌ {error}</div>;
  }

  return (
    <>
      {/* Column Customizer */}
      {showColumnCustomizer && showColumnCustomizerModal && (
        <div className="column-customizer" style={{ 
          maxHeight: '300px',
          overflowY: 'auto',
          marginBottom: 'var(--space-6)'
        }}>
          <h4 className="text-lg font-semibold" style={{ margin: '0 0 var(--space-4) 0' }}>Column Settings</h4>
          {columns.map((column, index) => (
            <div 
              key={column.key} 
              draggable
              onDragStart={(e) => handleCustomizerDragStart(e, index)}
              onDragOver={handleCustomizerDragOver}
              onDrop={(e) => handleCustomizerDrop(e, index)}
              className="column-item"
            >
              <span className="text-sm text-secondary" style={{ cursor: 'move', userSelect: 'none' }}>
                ☰
              </span>
              <label className="flex items-center gap-3 flex-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => toggleColumnVisibility(index)}
                  className="rounded"
                />
                <span style={{ 
                  fontWeight: column.visible ? 'var(--font-medium)' : 'var(--font-normal)', 
                  color: column.visible ? 'var(--text-primary)' : 'var(--text-secondary)'
                }}>
                  {column.label}
                </span>
              </label>
              <span className="text-xs text-secondary">
                {column.width}px
              </span>
            </div>
          ))}
          <button
            onClick={() => setShowColumnCustomizerModal(false)}
            className="btn btn-primary text-sm"
            style={{ marginTop: 'var(--space-4)' }}
          >
            Done
          </button>
        </div>
      )}

      {/* Actions Bar */}
      {(showColumnCustomizer || customActions) && (
        <div className="flex justify-between items-center" style={{ marginBottom: 'var(--space-4)' }}>
          <div>{customActions}</div>
          {showColumnCustomizer && (
            <button
              type="button"
              onClick={() => setShowColumnCustomizerModal(!showColumnCustomizerModal)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="table-container" style={{ margin: 'var(--space-6) 0' }}>
        <table className="modern-table" ref={tableRef}>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: `${column.width}px`, cursor: column.sortable ? 'pointer' : 'default' }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="header-content">
                    <span className="header-text">
                      {column.label} {column.sortable && getSortIcon(column.key)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} style={{ textAlign: 'center', padding: '40px' }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr 
                  key={item[idField]}
                  onClick={() => onRowClick?.(item)}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {selectable && (
                    <td style={{ width: '50px' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item[idField])}
                        onChange={() => toggleSelectItem(item[idField])}
                        className="rounded"
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td 
                      key={`${item[idField]}-${column.key}`}
                      style={{ width: `${column.width}px` }}
                    >
                      {column.render ? column.render(item, column) : item[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {data.length} of {pagination.total} items
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => onPageChange(1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              ⏮️ First
            </button>
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              ◀️ Previous
            </button>
            <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Page 
              <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                <input
                  type="number"
                  value={pageInput || pagination.page}
                  onChange={(e) => setPageInput(e.target.value)}
                  min="1"
                  max={pagination.totalPages}
                  style={{ 
                    width: '50px', 
                    padding: '2px 4px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    textAlign: 'center'
                  }}
                />
              </form>
              of {pagination.totalPages}
            </span>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              Next ▶️
            </button>
            <button
              onClick={() => onPageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              Last ⏭️
            </button>
          </div>
        </div>
      )}

      {/* Floating Selection UI */}
      {selectable && selectedIds.size > 0 && onDelete && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-4) var(--space-6)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            backdropFilter: 'blur(12px)',
            minWidth: '280px'
          }}
          className="floating-selection-bar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              {selectedIds.size}
            </div>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              {selectedIds.size} selected
            </span>
          </div>
          
          <button
            onClick={handleDeleteSelected}
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 107, 0.3)';
            }}
          >
            <span>🗑️</span>
            <span>Delete Selected</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedIds(new Set());
              setSelectAll(false);
            }}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Clear selection"
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

export default DataTable;